import classNames from "classnames";
import React, { memo, useCallback, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useLocation, useParams } from "react-router-dom";
import { Breadcrumbs } from "components/Shared";
import { Loading, ErrorState } from "./components/Visuals";
import { date, number } from "./format";
import { CountsResponse, DashboardProperty, PropertiesResponse, UnitCounts } from "./types";
import axios from "v2/utils/axios";
import { DashboardFreshness, useDashboardData } from "./useDashboardData";
import Overview from "./views/Overview";
import Projects from "./views/Projects";
import KeyMilestones from "./views/KeyMilestones";
import Units from "./views/Units";
import StageUnits from "./views/StageUnits";

export const BASE_PATH = "/executive-dashboard";

export interface ViewProps {
	asset: DashboardProperty | null;
	properties: DashboardProperty[];
	assetHref: (assetId: string | null) => string;
	viewHref: (key: string, params?: { [name: string]: string }) => string;
}

interface ViewDefinition {
	key: string;
	label: string;
	hint: string;
	eyebrow: string;
	title: (asset: DashboardProperty | null) => string;
	render: (props: ViewProps) => React.ReactNode;
}

const VIEWS: ViewDefinition[] = [
	{
		key: "overview",
		label: "Overview",
		hint: "Status snapshot",
		eyebrow: "Retail delivery tracker",
		title: (asset) => (asset ? asset.name : "All Assets"),
		render: (props) => <Overview {...props} />
	},
	{
		key: "design-stage",
		label: "Design Stage",
		hint: "Signed, in design",
		eyebrow: "Design stage : leases signed, not open",
		title: () => "Design Stage",
		render: (props) => <StageUnits stage="design" {...props} />
	},
	{
		key: "fitout-stage",
		label: "Fitout Stage",
		hint: "Signed, on site",
		eyebrow: "Fitout stage : leases signed, not open",
		title: () => "Fitout Stage",
		render: (props) => <StageUnits stage="fitout" {...props} />
	},
	{
		key: "in-delivery",
		label: "In Delivery",
		hint: "Projects & risk",
		eyebrow: "In delivery : active projects",
		title: () => "In Delivery : Active Projects",
		render: (props) => <Projects category="delivery" {...props} />
	},
	{
		key: "refurbishments",
		label: "Refurbishments",
		hint: "Refurb pipeline",
		eyebrow: "Refurbishments : active projects",
		title: () => "Refurbishments : Active Projects",
		render: (props) => <Projects category="refurbishment" {...props} />
	},
	{
		key: "terminations",
		label: "Terminations",
		hint: "Lease exits",
		eyebrow: "Terminations : upcoming vacancies",
		title: () => "Terminations",
		render: (props) => <Units mode="terminations" {...props} />
	},
	{
		key: "tenancy-schedule",
		label: "Tenancy Schedule",
		hint: "Unit & lease data",
		eyebrow: "Tenancy schedule : every unit",
		title: () => "Tenancy Schedule",
		render: (props) => <Units mode="schedule" {...props} />
	},
	{
		key: "vacant-units",
		label: "Vacant Units",
		hint: "No lease today",
		eyebrow: "Vacant units : not trading",
		title: () => "Vacant Units",
		render: (props) => <Units mode="vacant" {...props} />
	},
	{
		key: "key-milestones",
		label: "Key Milestone Data",
		hint: "Forecast vs actual",
		eyebrow: "Key milestone data : completed projects",
		title: () => "Key Milestone Data",
		render: (props) => <KeyMilestones {...props} />
	}
];

const ExecutiveDashboard = () => {
	const [version, setVersion] = useState(0);
	const [updatedAt, setUpdatedAt] = useState<string | null>(null);
	const [refreshing, setRefreshing] = useState(false);
	const report = useCallback((generatedAt: string) => setUpdatedAt((current) => (!current || generatedAt < current ? generatedAt : current)), []);
	const freshness = useMemo(() => ({ version, report }), [version, report]);

	const refresh = () => {
		setRefreshing(true);
		axios
			.post(`${process.env.REACT_APP_API_ENDPOINT_V2}executive_dashboard/refresh`)
			.then(() => {
				setUpdatedAt(null);
				setVersion((current) => current + 1);
			})
			.catch(() => undefined)
			.finally(() => setRefreshing(false));
	};

	return (
		<DashboardFreshness.Provider value={freshness}>
			<Shell updatedAt={updatedAt} refreshing={refreshing} onRefresh={refresh} />
		</DashboardFreshness.Provider>
	);
};

interface ShellProps {
	updatedAt: string | null;
	refreshing: boolean;
	onRefresh: () => void;
}

const Shell = ({ updatedAt, refreshing, onRefresh }: ShellProps) => {
	const { view: viewKey } = useParams<{ view?: string }>();
	const location = useLocation();
	const assetId = new URLSearchParams(location.search).get("asset");
	const { data, isLoading, error } = useDashboardData<PropertiesResponse>("properties", {});
	const { data: counts } = useDashboardData<CountsResponse>("counts", {}, { background: true, enabled: !!data });
	const countsFor = (id: string) => {
		const match = counts ? counts.properties.find((entry) => entry.id === id) : null;
		return match ? match.counts : null;
	};

	const view = VIEWS.find((candidate) => candidate.key === viewKey) || VIEWS[0];
	const properties = data ? data.properties : [];
	const asset = properties.find((property) => property.id === assetId) || null;

	const viewHref = (key: string, params: { [name: string]: string } = {}) => {
		const search = new URLSearchParams({ ...(asset ? { asset: asset.id } : {}), ...params }).toString();
		return `${BASE_PATH}${key === "overview" ? "" : `/${key}`}${search ? `?${search}` : ""}`;
	};
	const assetHref = (id: string | null) => `${BASE_PATH}${view.key === "overview" ? "" : `/${view.key}`}${id ? `?asset=${id}` : ""}`;

	return (
		<React.Fragment>
			<Helmet>
				<title>Executive Dashboard</title>
			</Helmet>
			<Breadcrumbs items={[{ title: "executive-dashboard" }]} />
			<div className="executive-dashboard">
				<aside className="ed-sidebar">
					<div className="ed-brand">Executive Dashboard</div>
					<nav className="ed-nav" aria-label="Views">
						<div className="ed-nav__heading">Views</div>
						{VIEWS.map((candidate) => (
							<Link
								key={candidate.key}
								to={viewHref(candidate.key)}
								className={classNames("ed-nav__item", { "ed-nav__item--active": candidate.key === view.key })}
							>
								<span className="ed-nav__marker" />
								<span>
									<span className="ed-nav__label">{candidate.label}</span>
									<span className="ed-nav__hint">{candidate.hint}</span>
								</span>
							</Link>
						))}
						<Link to="/sla-monitor" className="ed-nav__item">
							<span className="ed-nav__marker" />
							<span>
								<span className="ed-nav__label">SLA Monitor</span>
								<span className="ed-nav__hint">SLA performance</span>
							</span>
						</Link>
					</nav>
					<nav className="ed-nav" aria-label="Portfolio">
						<div className="ed-nav__heading">{data && data.company ? `${data.company.short_name || data.company.name} Portfolio` : "Portfolio"}</div>
						<Link to={assetHref(null)} className={classNames("ed-nav__item ed-nav__item--asset", { "ed-nav__item--active": !asset })}>
							<span className="ed-nav__marker" />
							<span className="ed-nav__label">All Assets</span>
							<NavCount counts={counts ? counts.total : null} />
						</Link>
						{properties.map((property) => (
							<Link
								key={property.id}
								to={assetHref(property.id)}
								title={property.name}
								className={classNames("ed-nav__item ed-nav__item--asset", {
									"ed-nav__item--active": asset && asset.id === property.id
								})}
							>
								<span className="ed-nav__marker" />
								<span className="ed-nav__label">{property.short_name}</span>
								<NavCount counts={countsFor(property.id)} />
							</Link>
						))}
					</nav>
				</aside>
				<main className="ed-main">
					<header className="ed-header">
						<div>
							<div className="ed-eyebrow">
								{view.eyebrow} · {date(data ? data.as_of : null, "DD MMM YYYY")}
							</div>
							<h1 className="ed-header__title">{view.title(asset)}</h1>
						</div>
						<div className="ed-header__actions">
							<button
								type="button"
								className="ed-refresh"
								onClick={onRefresh}
								disabled={refreshing}
								title="Figures are kept for up to 15 minutes. Refresh recalculates them from the latest data."
							>
								<span className={classNames("ed-refresh__icon", { "ed-refresh__icon--spinning": refreshing })}>↻</span>
								{refreshing ? "Refreshing…" : "Refresh data"}
								{!refreshing && updatedAt && <span className="ed-refresh__time">Updated {date(updatedAt, "HH:mm")}</span>}
							</button>
							<span className="ed-chip">
								<span className="ed-dot ed-tone--trading" />
								{asset ? asset.location || asset.name : `${properties.length} properties`}
							</span>
						</div>
					</header>
					<div className="ed-body">
						{isLoading && !data && <Loading />}
						{error && <ErrorState />}
						{data && view.render({ asset, properties, assetHref, viewHref })}
					</div>
				</main>
			</div>
		</React.Fragment>
	);
};

const NavCount = ({ counts }: { counts: UnitCounts | null }) =>
	counts ? (
		<span
			className="ed-nav__count"
			title={`${number(counts.trading)} trading + ${number(counts.design)} design + ${number(counts.fitout)} fit-out + ${number(counts.vacant)} vacant = ${number(counts.total)} (${number(counts.units.total)} units)`}
		>
			{number(counts.total)} <span className="ed-nav__units">({number(counts.units.total)})</span>
		</span>
	) : (
		<span className="ed-nav__count ed-nav__count--loading">…</span>
	);

export default memo(ExecutiveDashboard);
